import React from "react";
import { Box, Chip, Tooltip, Typography } from "@mui/material";
import NorthEastIcon from "@mui/icons-material/NorthEast";
import {
  computeProfileTags,
  ProfileTag,
  TagDimension,
  TagCta,
  PitchRow,
} from "./utils/profileTags";

// Neutral slate palette for coherent (non-hook) tags. Colors are intentionally
// NOT good/bad (no red/green) so these read as structural descriptors, not
// quality grades.
const DIMENSION_COLOR: Record<TagDimension, string> = {
  shape: "#3F5B78",
  consistency: "#5A6465",
  cohort: "#4A6152",
  context: "#6B5B73",
  pitch: "#4C5A78",
};

const DIMENSION_LABEL: Record<TagDimension, string> = {
  shape: "Profile Shape",
  consistency: "Signal Consistency",
  cohort: "Cohort Position",
  context: "Context Sensitivity",
  pitch: "Pitch-Type Read",
};

// Hook tags share one amber accent so a red-heavy scanner's eye catches the
// "wait, is this as good as I think?" moment regardless of which axis it lives
// on.
const HOOK_COLOR = "#B7791F";

const CTA_LABEL: Record<TagCta, string> = {
  context: "See MLB Context",
  comps: "See MLB Comps",
  splits: "See Splits",
};

// Same-page anchor for the hitter Pitch Type Performance card. Any pitch-type
// chip scrolls here rather than out to a separate page.
const PITCH_CARD_ANCHOR = "pitch-type-performance";

function ctaHref(cta: TagCta, id: string | undefined, isPitcher: boolean): string {
  switch (cta) {
    case "context":
      return `/mlb-context/${id}`;
    case "comps":
      return isPitcher ? `/pitcher-comparison/${id}` : `/mlb-comparison/${id}`;
    case "splits":
      return `/plus/splits/${id}`;
    default:
      return `/player/${id}`;
  }
}

/**
 * Renders the interpretation layer as a compact chip strip above the percentile
 * sliders. Coherent tags are neutral slate; certainty-interrupting "hook" tags
 * carry an amber accent and link out to the relevant deeper page (a gentle push
 * toward Comps / MLB Context / Splits, which are the PS+ conversion surfaces).
 */
export default function ProfileTags({
  percentiles,
  playerData,
  position,
  level,
  id,
  pitchProfile,
  isDesktop,
}: {
  percentiles: Record<string, unknown> | null | undefined;
  playerData: Record<string, unknown> | null | undefined;
  position: string | null | undefined;
  level: string | null | undefined;
  id?: string;
  pitchProfile?: PitchRow[] | null;
  isDesktop?: boolean;
}) {
  const tags: ProfileTag[] = React.useMemo(
    () => computeProfileTags(percentiles, playerData, position, level, pitchProfile),
    [percentiles, playerData, position, level, pitchProfile]
  );

  // Hook chips carry the monetization nudge, so order them first: if anything
  // has to be dropped to fit two rows, it should be a low-priority neutral tag,
  // never a hook. (Array.prototype.sort is stable, so same-priority tags keep
  // their original dimension order.)
  const orderedTags = React.useMemo(
    () =>
      [...tags].sort(
        (a, b) => (a.tone === "hook" ? 0 : 1) - (b.tone === "hook" ? 0 : 1)
      ),
    [tags]
  );

  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const [visibleCount, setVisibleCount] = React.useState(orderedTags.length);

  // Measure the real wrapped layout and only show chips that land on the first
  // two rows. Long labels wrap unpredictably, so a static count or max-height
  // can't guarantee two rows -- we compute the cutoff from actual chip positions
  // and re-check on resize. Overflow chips stay in the DOM (kept laid out so the
  // measurement stays stable) but are made invisible, so no clipped sliver of a
  // third row can appear.
  React.useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const measure = () => {
      const kids = Array.from(el.children) as HTMLElement[];
      if (kids.length === 0) return;
      const rowTops: number[] = [];
      for (const k of kids) {
        const top = k.offsetTop;
        if (!rowTops.some((t) => Math.abs(t - top) < 4)) rowTops.push(top);
      }
      rowTops.sort((a, b) => a - b);
      if (rowTops.length <= 2) {
        setVisibleCount(orderedTags.length);
        return;
      }
      const thirdRowTop = rowTops[2];
      let count = 0;
      for (const k of kids) {
        if (k.offsetTop >= thirdRowTop - 2) break;
        count++;
      }
      setVisibleCount(count);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [orderedTags]);

  if (orderedTags.length === 0) return null;

  const isPitcher = position === "P";

  // Two-row height cap as a hard backstop (border-box: +12px vertical padding);
  // the measurement above is what actually prevents a partial third row.
  const chipHeight = isDesktop ? 22 : 20;
  const rowGap = 6;
  const twoRowMaxHeight = chipHeight * 2 + rowGap + 12;

  return (
    <Box
      ref={containerRef}
      sx={{
        display: "flex",
        flexWrap: "wrap",
        alignContent: "flex-start",
        alignItems: "center",
        gap: `${rowGap}px`,
        maxHeight: `${twoRowMaxHeight}px`,
        overflow: "hidden",
        px: "12px",
        py: "6px",
        mb: "2px",
        borderBottom: "1px solid rgba(0,0,0,0.06)",
      }}
    >
      {orderedTags.map((tag, i) => {
        const hidden = i >= visibleCount;
        const isHook = tag.tone === "hook";
        const isPitchCard = tag.dimension === "pitch";
        const color = isHook ? HOOK_COLOR : DIMENSION_COLOR[tag.dimension];
        const href = isPitchCard
          ? `#${PITCH_CARD_ANCHOR}`
          : isHook && tag.cta
          ? ctaHref(tag.cta, id, isPitcher)
          : undefined;
        const ctaText = isPitchCard
          ? "See Pitch-Type Performance"
          : isHook && tag.cta
          ? CTA_LABEL[tag.cta]
          : null;

        const chip = (
          <Chip
            size="small"
            component={href ? "a" : "div"}
            href={href}
            clickable={!!href}
            aria-hidden={hidden}
            tabIndex={hidden ? -1 : undefined}
            label={
              <Box sx={{ display: "inline-flex", alignItems: "center", gap: "3px" }}>
                {tag.label}
                {href && <NorthEastIcon sx={{ fontSize: "0.72rem" }} />}
              </Box>
            }
            sx={{
              height: isDesktop ? "22px" : "20px",
              fontSize: isDesktop ? "0.72rem" : "0.66rem",
              fontWeight: 700,
              color,
              backgroundColor: isHook ? "rgba(183,121,31,0.08)" : "#fff",
              border: `1.5px solid ${color}`,
              cursor: href ? "pointer" : "help",
              textDecoration: "none",
              ...(hidden && { visibility: "hidden", pointerEvents: "none" }),
              "& .MuiChip-label": { px: "8px" },
              "&:hover": href
                ? {
                    backgroundColor: isHook
                      ? "rgba(183,121,31,0.16)"
                      : "rgba(76,90,120,0.10)",
                  }
                : {},
              "&::before": {
                content: '""',
                display: "inline-block",
                width: "6px",
                height: "6px",
                borderRadius: "50%",
                backgroundColor: color,
                marginLeft: "6px",
                marginRight: "-2px",
              },
            }}
          />
        );

        return (
          <Tooltip
            key={tag.label}
            arrow
            enterTouchDelay={0}
            title={
              <Box sx={{ py: "2px" }}>
                <Typography
                  sx={{
                    fontSize: "0.6rem",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    opacity: 0.7,
                    mb: "2px",
                  }}
                >
                  {DIMENSION_LABEL[tag.dimension]}
                </Typography>
                <Typography sx={{ fontSize: "0.75rem", lineHeight: 1.35 }}>
                  {tag.detail}
                </Typography>
                {href && ctaText && (
                  <Typography
                    sx={{
                      fontSize: "0.7rem",
                      fontWeight: 700,
                      mt: "4px",
                      color: "#F6C667",
                      display: "flex",
                      alignItems: "center",
                      gap: "3px",
                    }}
                  >
                    {ctaText}
                    <NorthEastIcon sx={{ fontSize: "0.72rem" }} />
                  </Typography>
                )}
              </Box>
            }
          >
            {chip}
          </Tooltip>
        );
      })}
    </Box>
  );
}
